import { Value } from "@sinclair/typebox/value";
import { useAuth as useAuthZudoku } from "zudoku/hooks";
import { OpenIdProviderDataSchema, type OpenIdProviderData } from "../utils/auth";

/**
 * Returns the Zudoku provider data typed as OpenID, or null when the user is
 * unauthenticated / hydrating / using a different auth provider. Never throws.
 *
 * The returned object is the same reference Zudoku holds in its auth store —
 * downstream `useMemo` deps can rely on `accessToken` identity for stability.
 *
 * Consumers must handle null (render a loading/permission state, or skip
 * token-dependent logic) instead of assuming a valid token is present.
 *
 * @returns null while auth is hydrating, for non-OpenID providers, or when
 *   Zudoku's providerData fails schema validation (logged in dev with a
 *   redacted summary — never the raw token material).
 */
export const useOpenIdProviderData = (): OpenIdProviderData | null => {
  const { providerData } = useAuthZudoku();
  if (!providerData) return null;
  if (!Value.Check(OpenIdProviderDataSchema, providerData)) {
    if (import.meta.env.DEV) {
      const firstError = Value.Errors(OpenIdProviderDataSchema, providerData).First();
      console.warn(
        "useOpenIdProviderData: providerData failed schema check",
        {
          keys:
            typeof providerData === "object" && providerData !== null
              ? Object.keys(providerData)
              : typeof providerData,
          path: firstError?.path,
          message: firstError?.message,
        },
      );
    }
    return null;
  }
  return providerData;
};
